import React, { useState } from "react"

import ApplicationStepper from "@/components/application/ApplicationStepper"
import { ContractReviewForm } from "@/components/application/form-sections"
import AppLayout from "@/components/layout/AppLayout"
import { Button } from "@/components/ui/button"
import { useActiveJob } from "@/contexts/ActiveJobContext"
import { useApplication } from "@/contexts/ApplicationContext"
import { useToast } from "@/hooks/use-toast"
import { useContractSubmissionHandlers } from "@/hooks/useContractSubmissionHandlers"

const ContractPage: React.FC = () => {
  const { job } = useActiveJob()
  const { application } = useApplication()
  const { toast } = useToast()
  const [signature, setSignature] = useState<string | null>(null)

  // Get application ID from context
  const applicationId = application?.applicationId || ""

  // Use the contract submission handlers
  const { handleContractSubmit, isSubmitting, isContractSubmitError, contractSubmitError } =
    useContractSubmissionHandlers(applicationId)

  const handleSign = (signatureData: string | null) => {
    setSignature(signatureData)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!signature) {
      toast({
        title: "Signature Required",
        description: "Please sign the contract before proceeding.",
        variant: "destructive",
      })
      return
    }

    if (!application?.basicDetails?.firstName || !application?.basicDetails?.lastName) {
      toast({
        title: "Missing Information",
        description: "Your name information is missing. Please complete your basic details first.",
        variant: "destructive",
      })
      return
    }
    const fullName =
      application?.basicDetails?.firstName + " " + application?.basicDetails?.lastName
    const address = application?.personalDetails?.address?.line1
      ? application?.personalDetails?.address?.line1 +
        ", " +
        application?.personalDetails?.address?.city +
        ", " +
        application?.personalDetails?.address?.postcode
      : ""
    const currentStage = job.stages.find((stage) => stage.id === "contract")
    const defaultSalary = currentStage?.meta?.defaultSalary as string
    try {
      // Submit the contract with signature via our new handler
      await handleContractSubmit(
        signature,
        application.basicDetails.firstName,
        application.basicDetails.lastName,
        application.airtablePersonId,
        application.personalDetails?.company?.number || fullName,
        address,
        application.personalDetails?.company?.vatNumber,
        application.contractSalary || defaultSalary,
      )

      // Toast is handled by the submission handler
    } catch (error) {
      console.error("Error submitting contract:", error)
      // Error toast is handled by the submission handler
    }
  }

  return (
    <AppLayout>
      <div className="container mx-auto px-4 py-6">
        <ApplicationStepper />

        <div className="mx-auto max-w-3xl">
          <div className="mb-6 text-center">
            <h1 className="mb-2 text-2xl font-bold text-jungle">Contract Review & Signing</h1>
            <p className="text-gray-600">
              Please review the contract below. Once you've read the contract, please sign it to
              proceed with your application. This will be emailed to you after you have signed and
              submitted.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <ContractReviewForm onSign={handleSign} signature={signature} />

            <div className="flex justify-center">
              <Button
                type="submit"
                disabled={!signature || isSubmitting}
                size="lg"
                className="bg-honeycomb hover:bg-amber-500"
              >
                {isSubmitting ? "Submitting..." : "Submit Signed Contract"}
              </Button>
            </div>

            {isContractSubmitError && (
              <div className="text-center text-red-500">
                Error:{" "}
                {contractSubmitError instanceof Error
                  ? contractSubmitError.message
                  : "Failed to submit contract"}
              </div>
            )}
          </form>
        </div>
      </div>
    </AppLayout>
  )
}

export default ContractPage
